import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, X, Download, Send, Ban } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const BADGE = { sent: "bg-sky-100 text-sky-800", viewed: "bg-amber-100 text-amber-800", signed: "bg-emerald-100 text-emerald-800", void: "bg-slate-200 text-slate-500" };
const LAWS = ["England and Wales", "Scotland", "Ireland", "United States (Delaware)", "United States (New York)", "Canada (Ontario)", "Australia (New South Wales)", "Singapore", "United Arab Emirates (DIFC)", "Germany", "France", "Netherlands", "South Africa"];

function SendNdaModal({ onSaved, onClose }) {
  const [f, setF] = useState({ company_name: "", signer_name: "", signer_title: "", signer_email: "",
    purpose: "evaluation of a potential business relationship", governing_law: "England and Wales" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/ndas", f);
      toast.success("NDA sent for signature");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-3" data-testid="nda-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Send NDA for signature</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div><label className="text-xs font-bold text-slate-500">Company name *</label>
          <input className={inputCls} required value={f.company_name} onChange={set("company_name")} data-testid="nda-company" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold text-slate-500">Signatory name *</label>
            <input className={inputCls} required value={f.signer_name} onChange={set("signer_name")} data-testid="nda-signer-name" /></div>
          <div><label className="text-xs font-bold text-slate-500">Job title</label>
            <input className={inputCls} value={f.signer_title} onChange={set("signer_title")} data-testid="nda-signer-title" /></div>
        </div>
        <div><label className="text-xs font-bold text-slate-500">Signatory email *</label>
          <input className={inputCls} type="email" required value={f.signer_email} onChange={set("signer_email")} data-testid="nda-signer-email" /></div>
        <div><label className="text-xs font-bold text-slate-500">Purpose</label>
          <input className={inputCls} value={f.purpose} onChange={set("purpose")} data-testid="nda-purpose" /></div>
        <div><label className="text-xs font-bold text-slate-500">Governing law</label>
          <select className={inputCls} value={f.governing_law} onChange={set("governing_law")} data-testid="nda-law">
            {LAWS.map((l) => <option key={l}>{l}</option>)}
          </select></div>
        <p className="text-xs text-slate-400">Voliciti Group Ventures Ltd's signature is applied automatically. The signatory receives a secure link valid for 14 days.</p>
        <button type="submit" disabled={busy} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="nda-send-btn">
          {busy ? "Sending…" : "Send NDA"}
        </button>
      </form>
    </div>
  );
}

export default function AdminNdas() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const load = async () => {
    try { setRows((await api.get("/admin/ndas")).data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const dl = async (n) => {
    try {
      const r = await api.get(`/admin/ndas/${n.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `NDA-${n.company_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const resend = async (n) => {
    try { await api.post(`/admin/ndas/${n.id}/resend`); toast.success("Signing link re-sent"); } catch (e) { toast.error(apiError(e)); }
  };
  const voidNda = async (n) => {
    if (!window.confirm(`Void the NDA for ${n.company_name}? The signing link stops working.`)) return;
    try { await api.post(`/admin/ndas/${n.id}/void`); await load(); toast.success("NDA voided"); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-ndas">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><FileSignature size={20} /> NDAs</h2>
        <button onClick={() => setShow(true)} className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 transition-colors" data-testid="nda-new-btn">+ Send NDA</button>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">Send a mutual NDA for electronic signature before sharing confidential information. Signed copies are emailed to both parties automatically with a full audit trail.</p>
      <div className="space-y-3">
        {rows.map((n) => (
          <div key={n.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`nda-row-${n.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold">{n.company_name}</p>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${BADGE[n.status] || BADGE.sent}`} data-testid={`nda-status-${n.id}`}>{n.status}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{n.signer_name}{n.signer_title && ` (${n.signer_title})`} · {n.signer_email}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Sent {(n.created_at || "").slice(0, 10)} · Law: {n.governing_law}
                {n.signed_at && <> · Signed {(n.signed_at || "").slice(0, 10)} by {n.signed_name}</>}
              </p>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <button onClick={() => dl(n)} className="inline-flex items-center gap-1.5 text-xs font-bold border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`nda-dl-${n.id}`}><Download size={12} /> PDF</button>
              {(n.status === "sent" || n.status === "viewed") && (
                <>
                  <button onClick={() => resend(n)} className="inline-flex items-center gap-1.5 text-xs font-bold border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg hover:bg-emerald-50" data-testid={`nda-resend-${n.id}`}><Send size={12} /> Resend</button>
                  <button onClick={() => voidNda(n)} className="inline-flex items-center gap-1.5 text-xs font-bold border border-red-200 text-red-600 px-3 py-2 rounded-lg hover:bg-red-50" data-testid={`nda-void-${n.id}`}><Ban size={12} /> Void</button>
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No NDAs yet — send your first one before a confidential conversation.</p>}
      </div>
      {show && <SendNdaModal onSaved={load} onClose={() => setShow(false)} />}
    </div>
  );
}
